import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Paper,
  Box,
  Grid,
} from '@mui/material';
import Api from '@api';
import CustomLabel from 'pages/common/CustomLabel';
import moment from 'moment';
import axios from 'axios';
import { useAuthUser } from '@crema/utility/AuthHooks';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AppLoader from '@crema/core/AppLoader';

const DataTable = () => {
  const { user } = useAuthUser();
  const navigate = useNavigate();
  const [filterData, setFilterData] = useState({
    date: moment().format('YYYY-MM-DD'),
  });
  const [data, setData] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [myActions, setMyActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const permissionCheck = useSelector(({ settings }) => settings.permissions);

  useEffect(() => {
    if (!permissionCheck) return;
    let sub_mod = {};
    permissionCheck?.map((el) => {
      if (el.subModuleName == 'Driver Benefits') sub_mod = el;
    });
    setMyActions(sub_mod?.actions);
    if (!sub_mod || !sub_mod?.actions?.includes('View'))
      navigate('/error-pages/error-404');
  }, [permissionCheck]);

  const handleInputChange = (e, rowIndex, field) => {
    const value = parseFloat(e.target.value);
    const updatedRows = data.map((row, index) => {
      if (index === rowIndex) {
        return { ...row, [field]: value };
      }
      return row;
    });

    const updatedValidationErrors = { ...validationErrors };
    if (
      updatedRows[rowIndex].approvedKm >
      updatedRows[rowIndex].actualDistnaceTraveledPerDay
    ) {
      updatedValidationErrors[`${rowIndex}-approvedKm`] =
        'Value cannot exceed actual distance.';
    } else {
      delete updatedValidationErrors[`${rowIndex}-approvedKm`];
    }
    if (updatedRows[rowIndex].approvedOTTime > updatedRows[rowIndex].oTTime) {
      updatedValidationErrors[`${rowIndex}-approvedOTTime`] =
        'Value cannot exceed actual OTA.';
    } else {
      delete updatedValidationErrors[`${rowIndex}-approvedOTTime`];
    }
    setData(updatedRows);
    setValidationErrors(updatedValidationErrors);

    setHasChanges(true);
  };

  const getAll = (fd) => {
    setData([]);
    setValidationErrors({});
    setIsLoading(true)
    axios
      .get(
        Api.baseUri +
          `/user-reg/trip-driver/getDriverBenefit?corporateId=${
            user?.userList?.corporateId
          }&date=${fd?.date}&vendorId=${
            user?.userList?.userRole === 'VENDOR'
              ? user?.userList?.profileId
              : ''
          }`
      )
      .then((res) => {
        setIsLoading(false)
        let tem = [];
        res?.data?.data?.map((el) => {
          tem.push({
            ...el,
            oTTime: el?.oTTime || 0,
            actualDistnaceTraveledPerDay: el?.actualDistnaceTraveledPerDay || 0,
          });
        });
        setData(tem || []);
        setHasChanges(false);
      })
      .catch((err) => {
        setIsLoading(false)
        console.error(err);
      });
  };

  useEffect(() => {
    if (filterData.date) getAll(filterData);
  }, []);

  const handleApprove = () => {
    if (Object.keys(validationErrors).length > 0) {
      alert('Please resolve all validation errors before approving.');
      return;
    }
    let resp = [];
    data?.map((el) => {
      resp.push({
        id: el?.id,
        corporateId: el?.corporateId,
        driverId: el?.driverId,
        date: el?.date,
        approvedKm: el?.approvedKm,
        approvedOTTime: el?.approvedOTTime,
        status: 'APPROVED',
      });
    });
    axios
      .put(Api.baseUri + '/user-reg/trip-driver/update-driver-benefit', resp)
      .then((res) => {
        if (res?.data?.status == '200') {
          toast.success('Approved successfully');
        } else {
          toast.error(res?.data?.message || 'Something went wrong');
        }
        getAll(filterData);
      })
      .catch((er) => {
        toast.error('Something went wrong');
        getAll(filterData);
      });
    setHasChanges(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && <AppLoader />}
      <Grid container spacing={2} sx={{ mb: 6 }} className="page-header-second">
        <Grid item xs={6}>
          <CustomLabel labelVal="Driver Benefit" variantVal="h3-underline" />
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mb: 6, mt: 4 }}>
        <Grid item xs={3}>
          <TextField
            InputLabelProps={{ shrink: true }}
            size="small"
            value={filterData?.date}
            fullWidth
            onChange={(e) => {
              setFilterData({ ...filterData, date: e.target.value });
            }}
            label="Date"
            type="date"
          />
        </Grid>
        <Grid item xs={3}>
          <Button
            variant="contained"
            onClick={() => {
              getAll(filterData);
            }}
          >
            Search
          </Button>
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApprove}
          disabled={!hasChanges || Object.keys(validationErrors).length > 0}
        >
          Approve
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Driver Name</TableCell>
              <TableCell>Vendor Name</TableCell>
              <TableCell>Actual Distance Travelled (KM)</TableCell>
              <TableCell>Approved Distance Travelled (KM)</TableCell>
              <TableCell>Actual Over Time Duration (Mins)</TableCell>
              <TableCell>Approved Over Time Duration (Mins)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.driverName}</TableCell>
                <TableCell>{row.vendorName}</TableCell>
                <TableCell>{row.actualDistnaceTraveledPerDay}</TableCell>
                <TableCell>
                  {row.status === 'PENDING' ? (
                    <TextField
                      size="small"
                      type="number"
                      sx={{ minWidth: '230px' }}
                      value={row.approvedKm}
                      onChange={(e) =>
                        handleInputChange(e, index, 'approvedKm')
                      }
                      error={!!validationErrors[`${index}-approvedKm`]}
                      helperText={validationErrors[`${index}-approvedKm`]}
                    />
                  ) : (
                    row.approvedKm
                  )}
                </TableCell>
                <TableCell>{row.oTTime}</TableCell>
                <TableCell>
                  {row.status === 'PENDING' ? (
                    <TextField
                      size="small"
                      type="number"
                      sx={{ minWidth: '230px' }}
                      value={row.approvedOTTime}
                      onChange={(e) =>
                        handleInputChange(e, index, 'approvedOTTime')
                      }
                      error={!!validationErrors[`${index}-approvedOTTime`]}
                      helperText={validationErrors[`${index}-approvedOTTime`]}
                    />
                  ) : (
                    row.approvedOTTime
                  )}
                </TableCell>
                <TableCell>{row.status}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DataTable;
